import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import type { ComponentProps, CSSProperties, HTMLAttributes, MouseEvent, ReactNode } from 'react';
import { canGoBack, currentScreen, hostedDoc } from '@/lib/nav-context';
import { screenIcon } from '@/app/screen-icons';
import { cn } from '@/lib/utils';

export function Card({ className, ...p }: ComponentProps<'div'>) {
  return <div className={cn('min-w-0 rounded-lg border border-border bg-surface shadow-(--shadow-card) transition-shadow duration-200', className)} {...p} />;
}

export function CardHeader({ title, description, actions, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-start justify-between gap-4 border-b border-border px-5 py-3.5', className)}>
      <div className="min-w-0">
        <h3 className="text-[15px] font-semibold">{title}</h3>
        {description && <p className="mt-0.5 text-[13px] text-fg-muted">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

// Status pills: tinted, with a hairline ring in the same colour (meaning is also in the word, never colour alone).
const badgeVariants = cva('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ring-1 ring-inset', {
  variants: {
    tone: {
      neutral: 'bg-black/[.05] text-fg-muted ring-black/[.08] dark:bg-white/[.06] dark:ring-white/10',
      primary: 'bg-primary-soft text-primary ring-primary/20',
      success: 'bg-success-soft text-success ring-success/25',
      warning: 'bg-warning-soft text-warning ring-warning/25',
      danger: 'bg-danger-soft text-danger ring-danger/25',
    },
  },
  defaultVariants: { tone: 'neutral' },
});
export function Badge({ tone, attention, className, ...p }: HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants> & { attention?: boolean }) {
  return <span className={cn(badgeVariants({ tone }), attention && 'fx-attn', className)} {...p} />;
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-5 animate-spin text-fg-subtle', className)} />;
}

export function Skeleton({ className, style }: { className?: string; style?: CSSProperties }) {
  return <div className={cn('fx-skeleton rounded-md', className)} style={style} />;
}

export function EmptyState({ icon, title, description, action }: { icon?: ReactNode; title: string; description?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      {icon && <div className="fx-pop mb-3 flex size-14 items-center justify-center rounded-2xl bg-linear-to-br from-primary-soft to-surface-2 text-primary shadow-(--shadow-card) ring-1 ring-border [&_svg]:size-6">{icon}</div>}
      <p className="fx-rise font-medium">{title}</p>
      {description && <p className="fx-rise mt-1 max-w-sm text-[13px] text-fg-muted">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function PageHeader({ title, description, actions, back }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; back?: ReactNode }) {
  // The screen's own icon beside its title, so every page is recognisable at a glance.
  const here = currentScreen();
  const Icon = here ? screenIcon(here.screen) : null;
  return (
    <div className="fx-rise mb-6 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        {back && <span onClickCapture={backInContext}>{back}</span>}
        <div className="flex items-center gap-3.5">
          {Icon && (
            <span aria-hidden className="hidden size-12 shrink-0 items-center justify-center rounded-2xl bg-linear-to-br from-primary to-accent text-white shadow-[0_10px_22px_-10px_var(--color-primary)] ring-1 ring-white/20 sm:flex [&_svg]:size-6">
              <Icon strokeWidth={1.9} />
            </span>
          )}
          <div className="min-w-0">
            <h1 className="text-[24px] leading-tight font-extrabold tracking-tight">{title}</h1>
            {description && <p className="mt-1 text-[13px] text-fg-muted">{description}</p>}
          </div>
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Alert({ tone = 'danger', title, children, className }: { tone?: 'danger' | 'warning' | 'success' | 'primary'; title?: string; children?: ReactNode; className?: string }) {
  const tones = {
    danger: 'border-danger/30 bg-danger-soft text-danger',
    warning: 'border-warning/30 bg-warning-soft text-warning',
    success: 'border-success/30 bg-success-soft text-success',
    primary: 'border-primary/25 bg-primary-soft text-primary',
  };
  return (
    <div role="alert" className={cn('rounded-md border px-3.5 py-2.5 text-[13px]', tones[tone], className)}>
      {title && <p className="font-semibold">{title}</p>}
      {children && <div className={cn(title && 'mt-0.5', 'text-fg/80')}>{children}</div>}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="rounded border border-border-strong bg-surface px-1.5 py-px font-sans text-[11px] text-fg-muted">{children}</kbd>;
}

/**
 * Every page's Back (owner rule): to the page it was opened from, with its filters and page — whatever screen that
 * was. Opened directly (new tab, typed address): a record hosted in another screen goes to that screen's list, any
 * other page follows its own link.
 */
function backInContext(e: MouseEvent) {
  const here = currentScreen();
  if (!here) return;
  if (canGoBack()) {
    e.preventDefault();
    e.stopPropagation();
    window.history.back();
  } else if (hostedDoc()) {
    e.preventDefault();
    e.stopPropagation();
    window.location.assign(`/m/${here.module}/${here.screen}`);
  }
}
